'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Headline, Reveal } from '../ui';

// Answers verified against the codebase. Note: Reclipt indexes the spoken words
// (transcript), not visual frames — copy avoids the "multimodal/visual" claim.
const faqs = [
  {
    q: 'What is Reclipt?',
    a: 'An AI search engine for your video library. Reclipt transforms raw footage into a conversational database you can query in plain language, and hands back the exact clip.',
  },
  {
    q: 'How does the search work?',
    a: 'Reclipt transcribes every word, then breaks each video into indexed moments. A streaming assistant runs keyword and semantic (vector) search across those moments to find what you mean, not just what you typed.',
  },
  {
    q: 'What kinds of video can I upload?',
    a: 'Any spoken video: interviews, trivia, podcasts, vlogs, tutorials, demos. If there’s talking, Reclipt indexes it into searchable moments.',
  },
  {
    q: 'Can I export clips?',
    a: 'Yes. Queue the moments you want and batch-export them. Each clip is trimmed to its exact in/out point and bundled into folders as MP4s, straight from the results.',
  },
  {
    q: 'What are Bits?',
    a: 'Your recurring formats. Reclipt automatically clusters every instance of a repeated format across your archive and counts them, so a running segment becomes one browsable, exportable collection.',
  },
  {
    q: 'Is my data secure?',
    a: 'Built with enterprise-grade auth (Clerk) and Supabase. Your data is encrypted and only accessible by your authorized team members.',
  },
];

function Item({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.05] backdrop-blur-2xl">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full cursor-pointer list-none items-center justify-between gap-4 p-6 text-left"
      >
        <span className="font-mk-body text-lg font-extrabold tracking-[-0.02em] text-mk-ink">
          {q}
        </span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} className="text-mk-muted">
          <ChevronDown className="h-5 w-5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <p className="px-6 pb-6 font-mk-body text-mk-muted">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FaqSection() {
  return (
    <section id="faq" className="mx-auto max-w-3xl px-6 py-28">
      <Reveal className="mb-16 text-center">
        <Headline className="text-4xl sm:text-5xl">
          Frequently Asked Questions
        </Headline>
      </Reveal>
      <div className="space-y-4">
        {faqs.map((f, i) => (
          <Reveal key={f.q} delay={i * 0.04}>
            <Item q={f.q} a={f.a} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}
